import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import DescriptionEditor from '@wix/da-shared-react/pkg/DescriptionEditor';
import useIsMobileOrTouch from '@wix/da-editor-tiptap/pkg/components/utils/useIsMobileOrTouch';
import { EditorTiptapContentReadyHandler } from '@wix/da-shared-react/pkg/DescriptionEditor/DescriptionEditor';
import LegacyDescriptionEditor from './_partials/LegacyDescriptionEditor';
import { useSubmitField } from '../../hooks/useSubmitField';
import { SubmitFormContext } from '../../context';
import { initAdapter, submitAdapter } from './adapters';
import { validator } from './validation';
import type { EditorApi, EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';

import s from './DescriptionField.scss';

const FIELD_NAME = 'descriptionState';

const DescriptionField: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { initResponse } = useContext(SubmitFormContext);
  const { id, error, field } = useSubmitField(FIELD_NAME, {
    initAdapter,
    submitAdapter,
    submitIfCleanOnEdit: true,
    validator,
  });
  const { setValue, getValues } = useFormContext();

  const descriptionHtml = initResponse?.deviation?.extended?.descriptionText?.html;

  const [isLegacyDescription, setIsLegacyDescription] = useState(
    () => descriptionHtml?.type === 'writer' && descriptionHtml.markup
  );
  const [initialContent, setInitialContent] = useState(isLegacyDescription ? null : field.value);

  // As generating JSON is expensive for every typing event, we lazily resolve
  // the editors save state on submit rather than on typing.
  const lazyEditorStateValueResolver = useRef<() => string>(undefined);
  const handleInitialContentReady: EditorTiptapContentReadyHandler = useCallback(
    (api: EditorApi) => {
      lazyEditorStateValueResolver.current = () => {
        return JSON.stringify(api.getStateForSave());
      };
      setValue(FIELD_NAME, lazyEditorStateValueResolver.current);
    },
    [setValue]
  );

  // Mobile has special case because it renders the viewer not the editor
  // so we need to register the resolver with the initial content
  const isMobileOrTouch = useIsMobileOrTouch();
  useEffect(() => {
    if (isMobileOrTouch) {
      lazyEditorStateValueResolver.current = () => {
        if (initialContent?.html?.markup) {
          return initialContent?.html?.markup;
        }
        return initialContent && JSON.stringify(initialContent);
      };
      setValue(FIELD_NAME, lazyEditorStateValueResolver.current);
    }
  }, [isMobileOrTouch, initialContent, setValue]);

  // Change is sane in tiptap so we can safely determine dirty from first change
  const handleChange: UseEditorOptions['onEditorChange'] = useCallback(
    editorState =>
      setValue(FIELD_NAME, getValues(FIELD_NAME), {
        shouldDirty: true,
      }),
    [getValues, setValue]
  );

  const onConvertToDraft = useCallback((editorState: EditorState) => {
    setInitialContent(editorState);
    setIsLegacyDescription('');
  }, []);

  return (
    <FormElement
      inputId={id}
      label={<InputLabelText>{t('deviationSubmit.form.description.label')}</InputLabelText>}
    >
      {isLegacyDescription ? (
        <LegacyDescriptionEditor
          descriptionHtml={descriptionHtml}
          onConvertToDraft={onConvertToDraft as any}
        />
      ) : (
        <DescriptionEditor
          className={s['root']}
          placeholder={t('deviationSubmit.form.description.placeholder')}
          initialContent={initialContent}
          onInitialContentReady={handleInitialContentReady}
          onChange={handleChange}
        />
      )}
      {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
    </FormElement>
  );
};

DescriptionField.displayName = 'DescriptionField';

export default DescriptionField;
